import React, { FC } from 'react';
import { Card } from 'Shared/components/Card';
import { Table } from 'Shared/components';
import { formatMoney, formatDateShort } from 'Shared/utils';
import styles from './styles.module.css';
import { InstallmentType, LineItemType } from '@clearcover/core-app-types';

type PolicyInstallmentPlanSectionType = {
  installments: Array<InstallmentType>;
};

export const PolicyInstallmentPlanSection: FC<PolicyInstallmentPlanSectionType> = ({
  installments,
}) => {
  return (
    <Card id={'installments-card'} title={'Installments'}>
      <Table
        headers={[
          'DUE DATE',
          'AMOUNT DUE',
          'DESCRIPTION',
          'PAYMENT DATE',
          'BALANCE',
        ]}
        headerClassName={styles.tableHeader}
        className={styles.installmentsTable}
        striped={false}
        columnWidths={[0.5, 1.3, 1, 0.5, 0.5]}
        columnClassNames={styles.installmentsColumn}
        rows={installments.map(
          (
            { dueDate, totalDue, description, draftDate, balance, lineItems },
            index
          ) => ({
            key: index,
            className: styles.installmentRow,
            data: [
              formatDateShort(dueDate),
              <AmountDueSection lineItems={lineItems} totalDue={totalDue} />,
              description,
              draftDate !== undefined ? formatDateShort(draftDate) : '',
              formatMoney(balance),
            ],
          })
        )}
      />
    </Card>
  );
};

type AmountDueSectionType = {
  lineItems: LineItemType[];
  totalDue: string;
};

type DisplayableLineItem = {
  label: string;
  amount: number;
};

const AmountDueSection: FC<AmountDueSectionType> = ({
  totalDue,
  lineItems,
}) => {
  const taxLineItems = lineItems.filter(lineItem => lineItem.type === 'TAX');
  const nonTaxLineItems = lineItems.filter(lineItem => lineItem.type !== 'TAX');
  const taxTotal = taxLineItems.reduce(
    (previous, current) => previous + current.amount.value,
    0
  );

  const displayableNonTaxLineItems = nonTaxLineItems
    .filter(lineItem => lineItem.amount.value !== 0)
    .map(lineItem => ({
      label: lineItem.label || lineItem.name,
      amount: lineItem.amount.value,
    }));

  const displayableTaxLineItems =
    taxTotal > 0 ? [{ label: 'Taxes', amount: taxTotal }] : [];

  const mergedLineItems: Array<DisplayableLineItem> = [
    ...displayableNonTaxLineItems,
    ...displayableTaxLineItems,
  ];

  return (
    <div className={styles.amountDueSectionContainer}>
      <div className={styles.totalDue}>Total: {formatMoney(totalDue)}</div>
      {mergedLineItems.length > 0 && (
        <div className={styles.premiumAndFeeContainer}>
          {mergedLineItems.map((lineItem, i) => (
            <div key={i}>
              {formatMoney(lineItem.amount)} {lineItem.label}
              {i < mergedLineItems.length - 1 && ' +'}&nbsp;
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
